import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { DecimalPipe } from '@angular/common';
import {
  AfterContentChecked,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatTabsModule } from '@angular/material/tabs';

/**
 * Count information to display in the tabs.
 */
export interface PlatformCatalogCountInfo {
  industryCatalogs: number;
  organizations: number;
  articles: number;
}

export type PlatformCatalogPanelTab = 'catalogs' | 'organizations';

@Component({
  selector: 'mp-platformcatalog-panel',
  templateUrl: './platformcatalog-panel.component.html',
  styleUrl: './platformcatalog-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DecimalPipe,
    ReactiveFormsModule,

    MatIcon,
    MatButton,
    MatInput,
    MatExpansionModule,
    MatFormFieldModule,
    MatTabsModule,
  ],
})
export class PlatformCatalogPanelComponent implements AfterContentChecked {
  private _expanded = false;
  private _disabled = false;
  private _editMode = false;
  private _checkFocus = false;
  private _activeTab: PlatformCatalogPanelTab = 'catalogs';
  private _formControl: FormControl<string>;

  constructor(private readonly formBuilder: FormBuilder) {
    this._formControl = this.createDefaultFormControl();
  }

  /**
   * Gets or sets whether the panel is expanded.
   */
  public get expanded(): boolean {
    return this._expanded;
  }
  @Input()
  public set expanded(value: BooleanInput) {
    this._expanded = coerceBooleanProperty(value);
  }

  /**
   * Gets or sets whether the panel can be expanded by the user.
   */
  public get disabled(): boolean {
    return this._disabled;
  }
  @Input()
  public set disabled(value: BooleanInput) {
    this._disabled = coerceBooleanProperty(value);
  }

  /**
   * Gets or sets whether the panel is in edit mode,
   * e.g. the name of the catalog can be set.
   */
  public get editMode(): boolean {
    return this._editMode;
  }
  @Input()
  public set editMode(value: BooleanInput) {
    const editMode = coerceBooleanProperty(value);
    if (this._editMode !== editMode) {
      this._checkFocus = this._editMode = editMode;
      this.editModeChange.emit(editMode);
    }
  }

  /**
   * If available, this form control is bound to the name field in edit mode.
   */
  public get nameControl(): FormControl<string> {
    return this._formControl;
  }
  @Input()
  public set nameControl(value: FormControl<string> | null | undefined) {
    this._formControl = value ?? this.createDefaultFormControl();
  }

  /**
   * Specifies the active tab.
   */
  public get activeTab(): PlatformCatalogPanelTab {
    return this._activeTab;
  }
  @Input()
  public set activeTab(value: PlatformCatalogPanelTab) {
    if (this._activeTab !== value) {
      this._activeTab = value;
      // this.activeTabChange.emit(value);
    }
  }

  /**
   * Information about the count numbers to display in the tabs.
   */
  @Input()
  public countInfo: PlatformCatalogCountInfo = {
    industryCatalogs: 0,
    organizations: 0,
    articles: 0,
  };

  /**
   * Fired after the panel got expaned.
   */
  @Output()
  public readonly afterExpand = new EventEmitter<void>();

  /**
   * Fired after the panel got collapsed.
   */
  @Output()
  public readonly afterCollapse = new EventEmitter<void>();

  /**
   * Fired when the active tab changes.
   */
  @Output()
  public readonly activeTabChange = new EventEmitter<PlatformCatalogPanelTab>();

  /**
   * Fires when the edit mode changes.
   */
  @Output()
  public readonly editModeChange = new EventEmitter<boolean>();

  /**
   * Fires when the name changes via edit mode.
   */
  @Output()
  public readonly save = new EventEmitter<string>();

  @ViewChild('nameInput')
  protected readonly inputElement?: ElementRef<HTMLInputElement>;

  ngAfterContentChecked(): void {
    if (this._checkFocus) {
      this._checkFocus = false;
      setTimeout(() => {
        const element = this.inputElement?.nativeElement;
        if (element && element !== element.ownerDocument.activeElement) {
          element.focus();
          element.select();
        }
      }, 0);
    }
  }

  protected onAfterExpand(): void {
    this.afterExpand.emit();
  }

  protected onAfterCollapse(): void {
    this.afterCollapse.emit();
  }

  protected onTabClick(tab: PlatformCatalogPanelTab): boolean {
    // setTimeout to keep the expaned state during navigation
    setTimeout(() => this.activeTabChange.emit(tab), 0);
    return !this.expanded;
  }

  protected onEditCancel(): void {
    this.editMode = false;
  }

  protected onEditSave(): void {
    this.save.emit(this._formControl.value.trim());
  }

  private createDefaultFormControl(): FormControl<string> {
    return this.formBuilder.control('', {
      nonNullable: true,
      validators: [Validators.required, Validators.pattern(new RegExp('\\S'))],
    });
  }
}
